import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSubjectById } from '../../config/subjects';
import { useAuth } from '../Auth/AuthContext';
import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function shuffleArray(array) {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function FlashcardsView() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const subject = getSubjectById(subjectId);

  const [decks, setDecks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selections, setSelections] = useState({});
  const [isPreparingMix, setIsPreparingMix] = useState(false);
  const [flashcards, setFlashcards] = useState([]);
  const [deckLabel, setDeckLabel] = useState('');
  const [isStudying, setIsStudying] = useState(false);

  useEffect(() => {
    if (!subject) {
      setError('Subject not found');
      setIsLoading(false);
      return;
    }

    fetchDecks();
  }, [subjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDecks = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_BASE_URL}/api/flashcards/decks?subjectId=${subject.wixId}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch decks');

      const data = await response.json();
      const fetchedDecks = data.decks || [];
      setDecks(fetchedDecks);
      setSelections(Object.fromEntries(fetchedDecks.map((d) => [d.deckId, 0])));
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch flashcard decks:', err);
      setError('Failed to load flashcard decks');
      setIsLoading(false);
    }
  };

  const handleSelectionChange = (deckId, value) => {
    setSelections((prev) => ({ ...prev, [deckId]: value }));
  };

  const totalSelected = Object.values(selections).reduce((sum, count) => sum + count, 0);

  const handleStartMix = async () => {
    if (!isSubscribed) {
      navigate('/pricing');
      return;
    }

    const chosenDecks = decks.filter((d) => (selections[d.deckId] || 0) > 0);
    if (chosenDecks.length === 0) return;

    setIsPreparingMix(true);
    setError('');

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const cardSets = await Promise.all(chosenDecks.map(async (deck) => {
        const response = await fetch(`${API_BASE_URL}/api/flashcards?deckId=${deck.deckId}`, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error('Failed to fetch flashcards');

        const data = await response.json();
        const cards = data.flashcards || [];
        const count = Math.min(selections[deck.deckId], cards.length);
        return shuffleArray(cards).slice(0, count);
      }));

      const mixedCards = shuffleArray(cardSets.flat());

      if (mixedCards.length === 0) {
        setError('No flashcards found for your selection');
        setIsPreparingMix(false);
        return;
      }

      setDeckLabel(
        chosenDecks.length === 1
          ? chosenDecks[0].name
          : `Custom Mix (${chosenDecks.length} decks)`
      );
      setFlashcards(mixedCards);
      setIsStudying(true);
    } catch (err) {
      console.error('Failed to build flashcard mix:', err);
      setError('Failed to load flashcards');
    } finally {
      setIsPreparingMix(false);
    }
  };

  const handleExitStudy = () => {
    setIsStudying(false);
    setFlashcards([]);
    setDeckLabel('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-quicksand">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-study-teal mx-auto mb-4"></div>
          <p className="text-study-black text-xl">Loading flashcard decks...</p>
        </div>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-quicksand">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-study-black mb-4">Subject Not Found</h1>
          <Link
            to="/academy"
            className="bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-yellow transition-all"
          >
            Back to Essentials
          </Link>
        </div>
      </div>
    );
  }

  // Study mode
  if (isStudying && flashcards.length > 0) {
    return (
      <FlashcardStudy
        deckLabel={deckLabel}
        initialFlashcards={flashcards}
        subject={subject}
        onExit={handleExitStudy}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white font-quicksand pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(`/academy/${subjectId}`)}
            className="flex items-center text-study-black hover:text-white transition-all mb-4"
          >
            <span className="text-2xl mr-2">←</span>
            <span className="font-bold">Back to {subject.name}</span>
          </button>
          <div className="flex items-center gap-4">
            <span className="text-5xl">{subject.icon}</span>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-study-black">
                {subject.name} Flashcards
              </h1>
              <p className="text-study-black text-lg">
                {decks.length} flashcard decks available — build your own mix below
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <div className="bg-red-200 p-4 rounded-lg text-center">
            <p className="text-study-black text-lg font-bold">{error}</p>
          </div>
        </div>
      )}

      {/* Premium Warning for Free Users */}
      {!isSubscribed && decks.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <div className="bg-study-yellow p-6 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-study-black mb-2">
              Premium Content
            </h3>
            <p className="text-study-black mb-4">
              Flashcards are available exclusively for Premium members
            </p>
            <Link
              to="/pricing"
              className="inline-block bg-study-teal text-study-black font-bold px-6 py-3 rounded-lg hover:bg-study-pink transition-all"
            >
              Upgrade to Premium
            </Link>
          </div>
        </div>
      )}

      {/* Flashcard Deck Mix Builder */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        {decks.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-study-black text-xl mb-4">
              No flashcard decks available yet for {subject.name}
            </p>
            <p className="text-gray-600">Check back soon for new content!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {decks.map((deck) => (
              <DeckMixCard
                key={deck.deckId}
                deck={deck}
                count={selections[deck.deckId] || 0}
                onChange={handleSelectionChange}
                isSubscribed={isSubscribed}
              />
            ))}
          </div>
        )}
      </div>

      {/* Start Studying Bar */}
      {isSubscribed && decks.length > 0 && (
        <div className="sticky bottom-4 mt-8 flex justify-center px-4">
          <div className="bg-study-black text-white rounded-full shadow-xl px-6 py-3 flex items-center gap-4">
            <span className="font-bold">
              {totalSelected} card{totalSelected === 1 ? '' : 's'} selected
            </span>
            <button
              onClick={handleStartMix}
              disabled={totalSelected === 0 || isPreparingMix}
              className="bg-study-teal text-study-black font-bold px-6 py-2 rounded-full hover:bg-study-yellow transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isPreparingMix ? 'Preparing...' : 'Start Studying'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DeckMixCard({ deck, count, onChange, isSubscribed }) {
  const max = deck.cardCount || 0;

  if (!isSubscribed) {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-gray-100 p-6">
        <div className="w-16 h-16 bg-gradient-to-br from-study-teal to-study-pink rounded-xl flex items-center justify-center mb-4">
          <span className="text-3xl">🗂️</span>
        </div>
        <h3 className="text-study-black font-bold text-xl mb-2">{deck.name}</h3>
        <p className="text-gray-600 mb-4">{deck.cardCount} cards</p>
        <div className="bg-study-yellow text-study-black px-4 py-2 rounded-lg font-bold text-center">
          Premium Only
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden border-2 border-gray-100 p-6">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-study-teal to-study-pink rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-3xl">🗂️</span>
        </div>
        <div>
          <h3 className="text-study-black font-bold text-lg">{deck.name}</h3>
          <p className="text-gray-600 text-sm">{max} cards available</p>
        </div>
      </div>

      <input
        type="range"
        min="0"
        max={max}
        value={count}
        onChange={(e) => onChange(deck.deckId, Number(e.target.value))}
        className="w-full accent-study-teal"
      />

      <div className="flex items-center justify-between mt-2">
        <span className="font-bold text-study-black">{count} selected</span>
        <div className="space-x-3">
          <button
            type="button"
            onClick={() => onChange(deck.deckId, max)}
            className="text-sm font-semibold text-study-teal hover:text-study-pink transition-all"
          >
            Pull All
          </button>
          <button
            type="button"
            onClick={() => onChange(deck.deckId, 0)}
            className="text-sm font-semibold text-gray-400 hover:text-gray-600 transition-all"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

function FlashcardStudy({ deckLabel, initialFlashcards, subject, onExit }) {
  const [sessionCards, setSessionCards] = useState(initialFlashcards);
  const [isReviewRound, setIsReviewRound] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [knownCards, setKnownCards] = useState([]);
  const [unknownCards, setUnknownCards] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const currentCard = sessionCards[currentIndex];
  const progress = (currentIndex / sessionCards.length) * 100;
  const isComplete = currentIndex >= sessionCards.length;

  const handleFlip = () => {
    if (isTransitioning) return;
    setIsFlipped(!isFlipped);
  };

  const handleKnow = () => {
    if (isTransitioning) return;
    setKnownCards([...knownCards, currentCard]);
    goToNext();
  };

  const handleDontKnow = () => {
    if (isTransitioning) return;
    setUnknownCards([...unknownCards, currentCard]);
    goToNext();
  };

  const goToNext = () => {
    setIsTransitioning(true);
    setIsFlipped(false);
    // Wait for the flip-back transition (duration-500) to finish before
    // swapping in the next card's content, otherwise the back face briefly
    // shows the next card's answer while it's still rotating into view.
    setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
      setIsTransitioning(false);
    }, 500);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownCards([]);
    setUnknownCards([]);
  };

  const handleReviewUnknown = () => {
    setSessionCards(shuffleArray(unknownCards));
    setIsReviewRound(true);
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownCards([]);
    setUnknownCards([]);
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isComplete) return;

      if (e.code === 'Space') {
        e.preventDefault();
        handleFlip();
      } else if (e.code === 'ArrowRight' && isFlipped) {
        handleKnow();
      } else if (e.code === 'ArrowLeft' && isFlipped) {
        handleDontKnow();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFlipped, currentIndex, isComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isComplete) {
    const accuracy = Math.round((knownCards.length / sessionCards.length) * 100);

    return (
      <div className="min-h-screen bg-gradient-to-br from-study-pink to-study-teal flex items-center justify-center font-quicksand p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold text-study-black mb-2">
            {isReviewRound ? 'Review Round Complete!' : 'Deck Complete!'}
          </h2>
          <p className="text-gray-600 mb-6">{deckLabel}</p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-100 p-4 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{knownCards.length}</p>
              <p className="text-green-600">Know It</p>
            </div>
            <div className="bg-red-100 p-4 rounded-lg">
              <p className="text-3xl font-bold text-red-600">{unknownCards.length}</p>
              <p className="text-red-600">Still Learning</p>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-5xl font-bold text-study-black">{accuracy}%</p>
            <p className="text-gray-600">Accuracy</p>
          </div>

          <div className="space-y-3">
            {unknownCards.length > 0 && (
              <button
                onClick={handleReviewUnknown}
                className="w-full bg-study-yellow text-study-black font-bold py-3 rounded-lg hover:bg-study-pink transition-all"
              >
                Review Still Learning ({unknownCards.length})
              </button>
            )}
            <button
              onClick={handleRestart}
              className="w-full bg-study-teal text-study-black font-bold py-3 rounded-lg hover:bg-study-yellow transition-all"
            >
              Study Again
            </button>
            <button
              onClick={onExit}
              className="w-full bg-gray-200 text-study-black font-bold py-3 rounded-lg hover:bg-gray-300 transition-all"
            >
              Back to Decks
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-study-pink to-study-teal font-quicksand">
      {/* Header */}
      <div className="p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={onExit}
            className="flex items-center text-study-black hover:text-white transition-all"
          >
            <span className="text-2xl mr-2">←</span>
            <span className="font-bold">Exit</span>
          </button>
          <div className="text-center">
            <h2 className="text-xl font-bold text-study-black">
              {isReviewRound ? 'Still Learning Review' : deckLabel}
            </h2>
            <p className="text-study-black text-sm">{subject.name}</p>
          </div>
          <div className="text-study-black font-bold">
            {currentIndex + 1} / {sessionCards.length}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto mt-4">
          <div className="h-2 bg-white bg-opacity-30 rounded-full overflow-hidden">
            <div
              className="h-full bg-study-yellow transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Flashcard */}
      <div className="flex-1 flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <div
          onClick={handleFlip}
          className="w-full max-w-2xl cursor-pointer perspective-1000"
        >
          <div
            className={`relative w-full transition-transform duration-500 transform-style-preserve-3d ${
              isFlipped ? 'rotate-y-180' : ''
            }`}
            style={{
              minHeight: '400px',
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            {/* Front of Card */}
            <div
              className="absolute inset-0 bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center justify-center backface-hidden"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <p className="text-sm text-gray-500 mb-4">QUESTION</p>
              <p className="text-2xl md:text-3xl text-study-black text-center font-medium">
                {currentCard.front}
              </p>
              <p className="text-gray-400 mt-8 text-sm">Tap or press Space to flip</p>
            </div>

            {/* Back of Card */}
            <div
              className="absolute inset-0 bg-study-yellow rounded-2xl shadow-2xl p-8 flex flex-col items-center justify-center"
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <p className="text-sm text-study-black opacity-60 mb-4">ANSWER</p>
              <p className="text-2xl md:text-3xl text-study-black text-center font-medium">
                {currentCard.back}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="p-4 pb-8">
        <div className="max-w-md mx-auto">
          {isFlipped ? (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={handleDontKnow}
                className="bg-white text-red-500 font-bold py-4 rounded-xl hover:bg-red-100 transition-all flex items-center justify-center gap-2"
              >
                <span>←</span> Still Learning
              </button>
              <button
                onClick={handleKnow}
                className="bg-white text-green-500 font-bold py-4 rounded-xl hover:bg-green-100 transition-all flex items-center justify-center gap-2"
              >
                Know It <span>→</span>
              </button>
            </div>
          ) : (
            <button
              onClick={handleFlip}
              className="w-full bg-white text-study-black font-bold py-4 rounded-xl hover:bg-study-yellow transition-all"
            >
              Tap to Reveal Answer
            </button>
          )}
        </div>
        <p className="text-center text-study-black text-sm mt-4 opacity-75">
          Keyboard: Space to flip, ← Still Learning, → Know It
        </p>
      </div>
    </div>
  );
}

export default FlashcardsView;
